import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Container,
  Typography,
  Box,
  Grid,
  TextField,
  Button,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

const apiUrl = process.env.REACT_APP_API_URL;

function normalizeArabic(str = '') {
  // إزالة التشكيل وتوحيد بعض أشكال الألف/الياء/التاء المربوطة
  return String(str)
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u064B-\u0652]/g, '') // التشكيل
    .replace(/[آأإ]/g, 'ا')
    .replace(/ى/g, 'ي')
    .replace(/ة/g, 'ه')
    .trim();
}

function safe(str) {
  return String(str || '').trim();
}

// محاولات مرنة لقراءة أسماء الحصان
function getHorseNameAr(h) {
  return (
    h.horse_name_ar || h.horseNameAr || h.name_ar || h.ar_name || '' // قد لا يتوفر العربي
  );
}
function getHorseNameEn(h) {
  return (
    h.horse_name_en ||
    h.horse_name || // أغلب مشاريعك تستخدم هذا كإنجليزي
    h.horseName ||
    h.name_en ||
    h.name ||
    ''
  );
}
function getOwnerName(h) {
  return h.owner_name || h.ownerName || h.client_name || '';
}

function buildHorseLabel(h) {
  const ar = safe(getHorseNameAr(h));
  const en = safe(getHorseNameEn(h));
  const owner = safe(getOwnerName(h));
  const ownerTrail = owner ? ` - ${owner}` : '';
  if (ar && en) return `${ar} (${en})${ownerTrail}`;
  return `${ar || en}${ownerTrail}`;
}

const HorseTransportForm = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [horses, setHorses] = useState([]);
  const [form, setForm] = useState({
    driver_name: '',
    driver_phone: '',
    from_location: '',
    to_location: '',
    horse_condition: '',
    notes: '',
    total_cost: '',
    profit_margin: '',
    selectedHorses: [], // [{ horse_id, client_id, owner_name }]
  });

  // collator لفرز عربي/إنجليزي
  const collator = useMemo(
    () =>
      new Intl.Collator(['ar', 'en'], { sensitivity: 'base', numeric: true }),
    []
  );

  // جلب الخيول
  useEffect(() => {
    (async () => {
      const res = await axios.get(`${apiUrl}/horses/inside`);
      setHorses(res.data || []);
    })();
  }, []);

  // قائمة مرتبة أبجديًا
  const sortedHorses = useMemo(() => {
    const copy = [...horses];
    copy.sort((a, b) =>
      collator.compare(buildHorseLabel(a), buildHorseLabel(b))
    );
    return copy;
  }, [horses, collator]);

  const handleHorseChange = (index, key, value) => {
    setForm((prev) => {
      const updated = [...prev.selectedHorses];
      updated[index] = { ...updated[index], [key]: value };
      return { ...prev, selectedHorses: updated };
    });
  };

  const addHorse = () => {
    setForm((prev) => ({
      ...prev,
      selectedHorses: [
        ...prev.selectedHorses,
        { horse_id: '', client_id: '', owner_name: '' },
      ],
    }));
  };

  const handleSubmit = async () => {
    try {
      const payload = {
        ...form,
        total_cost: parseFloat(form.total_cost || 0),
        profit_margin: parseFloat(form.profit_margin || 0),
        horses: form.selectedHorses,
      };
      await axios.post(`${apiUrl}/transport`, payload);
      alert('✅ تم الحفظ بنجاح');
      navigate('/HorseTransportReportPage');
    } catch (e) {
      console.error(e);
      alert('❌ حدث خطأ أثناء الحفظ');
    }
  };

  const costPerHorse = () => {
    const total = parseFloat(form.total_cost || 0);
    const margin = parseFloat(form.profit_margin || 0);
    const count = form.selectedHorses.length || 1;
    return ((total + margin) / count).toFixed(2);
  };

  return (
    <Container component="main" maxWidth="xs" variant="form">
      <Typography variant="h5" mb={4}>
        {t('horse_transport.title')}
      </Typography>

      <Grid container spacing={2}>
        {/* السائق */}
        <Grid item xs={6}>
          <TextField
            fullWidth
            label={t('horse_transport.driver_name')}
            value={form.driver_name}
            onChange={(e) => setForm({ ...form, driver_name: e.target.value })}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label={t('horse_transport.driver_phone')}
            value={form.driver_phone}
            onChange={(e) => setForm({ ...form, driver_phone: e.target.value })}
          />
        </Grid>

        {/* من - إلى */}
        <Grid item xs={6}>
          <TextField
            fullWidth
            label={t('horse_transport.from_location')}
            value={form.from_location}
            onChange={(e) =>
              setForm({ ...form, from_location: e.target.value })
            }
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label={t('horse_transport.to_location')}
            value={form.to_location}
            onChange={(e) => setForm({ ...form, to_location: e.target.value })}
          />
        </Grid>

        {/* التكلفة والربح */}
        <Grid item xs={6}>
          <TextField
            fullWidth
            label={t('horse_transport.total_cost')}
            type="number"
            value={form.total_cost}
            onChange={(e) => setForm({ ...form, total_cost: e.target.value })}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            label={t('horse_transport.profit_margin')}
            type="number"
            value={form.profit_margin}
            onChange={(e) =>
              setForm({ ...form, profit_margin: e.target.value })
            }
          />
        </Grid>

        {/* حالة الخيول */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            label={t('horse_transport.horse_condition')}
            value={form.horse_condition}
            onChange={(e) =>
              setForm({ ...form, horse_condition: e.target.value })
            }
          />
        </Grid>

        {/* الملاحظات */}
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label={t('horse_transport.notes')}
            value={form.notes}
            onChange={(e) => setForm({ ...form, notes: e.target.value })}
          />
        </Grid>
      </Grid>

      {/* قائمة الخيول */}
      <Box mt={5}>
        <Typography variant="h6" gutterBottom>
          {t('horse_transport.horse_list')}
        </Typography>

        {form.selectedHorses.map((row, index) => {
          const valueObj =
            horses.find((h) => h.horse_id === row.horse_id) || null;

          return (
            <Grid container spacing={2} key={index} mt={1}>
              <Grid item xs={12}>
                <Autocomplete
                  options={sortedHorses}
                  value={valueObj}
                  // منع اختيار نفس الخيل في صف آخر
                  getOptionDisabled={(opt) =>
                    form.selectedHorses.some(
                      (sh, i) => i !== index && sh.horse_id === opt.horse_id
                    )
                  }
                  isOptionEqualToValue={(opt, val) =>
                    opt?.horse_id === val?.horse_id
                  }
                  getOptionLabel={buildHorseLabel}
                  noOptionsText={t('no_options') || 'لا توجد نتائج'}
                  // فلترة مخصصة: عربي/إنجليزي/اسم المالك
                  filterOptions={(options, { inputValue }) => {
                    const q = normalizeArabic(inputValue);
                    if (!q) return options;
                    return options.filter((o) => {
                      const ar = normalizeArabic(getHorseNameAr(o));
                      const en = normalizeArabic(getHorseNameEn(o));
                      const owner = normalizeArabic(getOwnerName(o));
                      return (
                        ar.includes(q) || en.includes(q) || owner.includes(q)
                      );
                    });
                  }}
                  onChange={(_, newValue) => {
                    if (!newValue) {
                      // مسح السطر
                      handleHorseChange(index, 'horse_id', '');
                      handleHorseChange(index, 'client_id', '');
                      handleHorseChange(index, 'owner_name', '');
                      return;
                    }
                    // تعبئة الحقول
                    handleHorseChange(index, 'horse_id', newValue.horse_id);
                    handleHorseChange(index, 'client_id', newValue.owner_id);
                    handleHorseChange(
                      index,
                      'owner_name',
                      getOwnerName(newValue)
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label={t('horse_transport.select_horse')}
                      placeholder="ابحث بالعربي أو الإنجليزي أو اسم المالك"
                      fullWidth
                    />
                  )}
                />
              </Grid>

              <Grid item xs={6}>
                <TextField
                  fullWidth
                  disabled
                  label={t('horse_transport.select_owner')}
                  value={row.owner_name || ''}
                />
              </Grid>
              <Grid item xs={6}>
                <TextField
                  fullWidth
                  disabled
                  label={t('horse_transport.cost_per_horse')}
                  value={costPerHorse()}
                />
              </Grid>
            </Grid>
          );
        })}

        <Button onClick={addHorse} variant="outlined" sx={{ mt: 2 }}>
          {t('horse_transport.add_horse')}
        </Button>
      </Box>

      <Button
        variant="contained"
        color="primary"
        onClick={handleSubmit}
        sx={{ mt: 4 }}
      >
        {t('horse_transport.submit')}
      </Button>
    </Container>
  );
};

export default HorseTransportForm;
